/* import __COLOCATED_TEMPLATE__ from './topic-trends.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { AsyncData } from 'embercom/resources/utils/async-data';
import { use } from 'ember-resources/util/function-resource';
import {
  fetchTrendingTopics,
  fetchAvailableWeekStarts,
  type TrendingTopic,
  TrendingTopicTimeTrend,
} from './topic-trends/trending-topics-data-resources';
import { tracked } from '@glimmer/tracking';
import type { Topic } from '../../ai-insights/fin-insights/topics-data-resources';
import type ReportingChartService from 'embercom/services/reporting-chart-service';
import moment from 'moment-timezone';
import { isBlank } from '@ember/utils';
import { metricConfigs } from './topics-explorer/common';
import { insightsMetrics } from '../../ai-insights/fin-insights/jobs';
import MetricPickerItem from './topics-explorer/metric-picker-item';
import { next } from '@ember/runloop';

export default class TopicTrends extends Component {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare intercomEventService: $TSFixMe;
  @service declare permissionsService: any;
  @service declare reportingChartService: ReportingChartService;
  @service declare notificationsService: $TSFixMe;

  @tracked showTrendingTopicsSidePanel = false;
  @tracked exploreTrendingTopic: TrendingTopic | undefined = undefined;
  @tracked exploreConversationCount = 0;
  @tracked selectedTimeTrends: string[] = [];
  @tracked selectedPrimaryMetrics: string[] = [];
  @tracked selectedWeekStart: string | undefined = undefined;
  @tracked private hasCheckedUrlParams = false;

  private urlParamSubtopic: string | null;
  private urlParamWeek: string | null;

  constructor(owner: unknown, args: any) {
    super(owner, args);

    let urlParams = new URLSearchParams(window.location.search);
    this.urlParamSubtopic = urlParams.get('trendingTopic');
    this.urlParamWeek = urlParams.get('trendingTopicWeek');
  }

  @use trendingTopics = AsyncData<TrendingTopic[]>(({ abortSignal }) => {
    return fetchTrendingTopics(
      this.appService.app.id,
      this.reportingChartService.syntheticDataUseCase,
      abortSignal,
      this.selectedWeekStart,
    );
  });

  @action
  tryOpenTopicFromUrl() {
    if (!this.shouldProcessUrlParams()) {
      return;
    }

    next(() => {
      this.processUrlParamsWhenReady();
    });
  }

  private shouldProcessUrlParams(): boolean {
    return !this.hasCheckedUrlParams && !!this.urlParamSubtopic && !!this.urlParamWeek;
  }

  private processUrlParamsWhenReady() {
    if (!this.shouldProcessUrlParams()) {
      return;
    }

    if (!this.isDataLoaded()) {
      return;
    }

    if (!this.validateAndSyncWeek()) {
      return;
    }

    this.findAndOpenMatchingTopic();
  }

  private isDataLoaded(): boolean {
    return !this.weekStarts.isLoading && !!this.weekStarts.value && !this.trendingTopics.isLoading;
  }

  private validateAndSyncWeek(): boolean {
    if (this.weekStarts.value!.length > 0) {
      if (!this.weekStarts.value!.includes(this.urlParamWeek!)) {
        this.handleInvalidWeek();
        return false;
      }
    }

    if (this.selectedWeekStart !== this.urlParamWeek) {
      this.selectedWeekStart = this.urlParamWeek ?? undefined;
      return false;
    }

    return true;
  }

  private handleInvalidWeek() {
    this.showErrorNotification();
    this.clearUrlParams();
    this.hasCheckedUrlParams = true;
  }

  private findAndOpenMatchingTopic() {
    if (!this.trendingTopics.value || this.trendingTopics.value.length === 0) {
      this.handleNoTopicsFound();
      return;
    }

    let matchingTopic = this.trendingTopics.value.find(
      (topic) =>
        topic.subtopicName === this.urlParamSubtopic && topic.weekStart === this.urlParamWeek,
    );

    if (matchingTopic) {
      this.onShowTrendingTopicsPanel(matchingTopic);
    } else {
      this.showErrorNotification();
    }

    this.clearUrlParams();
    this.hasCheckedUrlParams = true;
  }

  private handleNoTopicsFound() {
    this.showErrorNotification();
    this.selectedWeekStart = undefined;
    this.clearUrlParams();
    this.hasCheckedUrlParams = true;
  }

  private showErrorNotification() {
    this.notificationsService.notifyError(
      this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topic-trends.url-params-not-found',
      ),
    );
  }

  private clearUrlParams() {
    let url = new URL(window.location.href);
    url.searchParams.delete('trendingTopic');
    url.searchParams.delete('trendingTopicWeek');
    window.history.replaceState({}, '', url.toString());
  }

  get timeTrendFilterItems() {
    let translationFor = (name: string) => {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.filters.items.time-trend.${name}`,
      );
    };
    return [
      {
        items: [
          {
            text: translationFor('trend'),
            value: 'trend',
            isSelected: this.selectedTimeTrends.includes('trend'),
          },
          {
            text: translationFor('single-week-anomaly'),
            value: 'anomaly',
            isSelected: this.selectedTimeTrends.includes('anomaly'),
          },
        ],
      },
    ];
  }

  get primaryMetricFilterItems() {
    let translationFor = (name: string) => {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.filters.items.primary-metric.${name}`,
      );
    };

    let metrics = insightsMetrics(this.intl);
    let configs = metricConfigs(this.intl);

    return [
      {
        items: [
          {
            text: translationFor('volume'),
            value: 'v1.new_conversations',
            isSelected: this.selectedPrimaryMetrics.includes('v1.new_conversations'),
            component: MetricPickerItem,
            componentAttrs: { color: 'bg-vis-mustard-40' },
          },
          {
            text: translationFor('fin-resolution-rate'),
            value: 'fin.resolution_rate',
            isSelected: this.selectedPrimaryMetrics.includes('fin.resolution_rate'),
            component: MetricPickerItem,
            componentAttrs: {
              color: configs[metrics.resolutionRate.metric_id].metricPickerItemClass,
            },
          },
          {
            text: translationFor('cx-score'),
            value:
              'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
            isSelected: this.selectedPrimaryMetrics.includes(
              'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
            ),
            component: MetricPickerItem,
            componentAttrs: {
              color: configs[metrics.aiCsatScore.metric_id].metricPickerItemClass,
            },
          },
          {
            text: translationFor('fin-involvement-rate'),
            value: 'fin.participation_rate',
            isSelected: this.selectedPrimaryMetrics.includes('fin.participation_rate'),
            component: MetricPickerItem,
            componentAttrs: {
              color: configs[metrics.involvementRate.metric_id].metricPickerItemClass,
            },
          },
        ],
      },
    ];
  }

  get filteredTrendingTopics(): TrendingTopic[] {
    let topics = this.trendingTopics.value ?? [];

    if (this.selectedPrimaryMetrics.length > 0) {
      topics = topics.filter((topic) => this.selectedPrimaryMetrics.includes(topic.primaryMetric));
    }

    if (this.selectedTimeTrends.length > 0) {
      topics = topics.filter((topic) => {
        return this.selectedTimeTrends.some((selectedTrend) => {
          if (selectedTrend === 'trend') {
            let trendTypes = [
              TrendingTopicTimeTrend.IncreasingTrend,
              TrendingTopicTimeTrend.DecreasingTrend,
              TrendingTopicTimeTrend.Flat,
            ];
            return trendTypes.includes(topic.timeTrend);
          } else if (selectedTrend === 'anomaly') {
            return topic.timeTrend === TrendingTopicTimeTrend.SingleWeekAnomaly;
          }
          return false;
        });
      });
    }
    return topics;
  }

  get selectedTimeTrendName() {
    if (this.selectedTimeTrends.length === 0) {
      return this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topic-trends.filters.items.time-trend.all',
      );
    }

    let selectedItems = this.timeTrendFilterItems[0].items.filter((item) =>
      this.selectedTimeTrends.includes(item.value),
    );

    if (selectedItems.length === 1) {
      return selectedItems[0].text;
    }

    return `${selectedItems[0].text} +${selectedItems.length - 1}`;
  }

  get selectedPrimaryMetricName() {
    if (this.selectedPrimaryMetrics.length === 0) {
      return this.intl.t(
        'components.reporting.automation.fin-insights.analyse.topic-trends.filters.items.primary-metric.all',
      );
    }

    let selectedItems = this.primaryMetricFilterItems[0].items.filter((item) =>
      this.selectedPrimaryMetrics.includes(item.value),
    );

    if (selectedItems.length === 1) {
      return selectedItems[0].text;
    }

    return `${selectedItems[0].text} +${selectedItems.length - 1}`;
  }

  @use weekStarts = AsyncData<string[]>(() => {
    return fetchAvailableWeekStarts(this.appService.app.id);
  });

  get defaultWeekStart(): string {
    let lastMonday = moment().subtract(1, 'week').startOf('isoWeek');
    return lastMonday.format('YYYY-MM-DD');
  }

  get weekStartOptions() {
    let weekStarts = this.weekStarts.value?.length
      ? this.weekStarts.value
      : [this.defaultWeekStart];

    return weekStarts.map((weekStart) => ({
      value: weekStart,
      text: this.formatWeekStartRange(weekStart),
      icon: 'calendar',
    }));
  }

  get selectedWeekStartValue(): string | undefined {
    return this.selectedWeekStart ?? this.trendingTopics.value?.[0]?.weekStart;
  }

  get disableWeekStartSelection(): boolean {
    return isBlank(this.trendingTopics.value);
  }

  get selectedWeekStartLabel(): string {
    if (this.selectedWeekStart) {
      return this.formatWeekStartRange(this.selectedWeekStart);
    }

    return this.formatWeekStartRange(
      this.trendingTopics.value?.[0]?.weekStart ?? this.defaultWeekStart,
    );
  }

  private formatWeekStartRange(weekStart: string): string {
    let startDate = new Date(weekStart);
    let endDate = new Date(startDate);
    endDate.setDate(startDate.getDate() + 6); // Add 6 days to get Sunday

    let formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };

    let startFormatted = this.intl.formatDate(startDate, formatOptions);
    let endFormatted = this.intl.formatDate(endDate, formatOptions);

    return `${startFormatted} - ${endFormatted}`;
  }

  get serializedReport() {
    // We use a long time range to ensure outlier conversations are found
    return {
      id: 'topic-trends',
      date_range: {
        selection: 'past_12_weeks',
      },
    };
  }

  get shouldShowEmptyState() {
    return !this.trendingTopics.isLoading && this.trendingTopics.value?.length === 0;
  }

  get shouldShowEmptyFilterState() {
    return !this.trendingTopics.isLoading && this.filteredTrendingTopics.length === 0;
  }

  get canAccessDrillIn() {
    return this.permissionsService.currentAdminCan('can_reporting__drillin__access');
  }

  get exploreSelectedTopic(): Topic {
    // Returns topic in the shape that the conversation explore panel expects
    return {
      name: this.exploreTrendingTopic?.topicName ?? '',
      description: null,
      subtopics: {},
    };
  }

  get exploreSelectedSubTopic(): string {
    return this.exploreTrendingTopic?.subtopicName ?? '';
  }

  @action
  onShowTrendingTopicsPanel(topic: TrendingTopic) {
    if (!this.canAccessDrillIn) {
      this.permissionsService.loadAllAdminsAndShowPermissionRequestModal(
        'can_reporting__drillin__access',
      );
      return;
    }

    this.exploreTrendingTopic = topic;
    this.showTrendingTopicsSidePanel = true;
    this.exploreConversationCount = topic.outlierConversationIds.length;
    this.trackAnalyticsEvent('trending-topics-opened', 'trending-topics', {
      topic: this.exploreTrendingTopic.topicName,
      subtopic: this.exploreTrendingTopic.subtopicName,
      week_start: this.exploreTrendingTopic.weekStart,
    });
  }

  @action
  closeTrendingTopics() {
    this.showTrendingTopicsSidePanel = false;
    this.exploreTrendingTopic = undefined;
    this.trackAnalyticsEvent('trending-topics-closed', 'trending-topics');
  }

  @action
  selectWeekStart(weekStart: string) {
    this.selectedWeekStart = weekStart;
    this.trackAnalyticsEvent('selected', 'week-start-filter', {
      week_start: weekStart,
    });
  }

  @action
  onTimeTrendSelectionChange(selectedValues: string[]) {
    let previousSelection = this.selectedTimeTrends;
    this.selectedTimeTrends = selectedValues;

    let added = selectedValues.filter((v) => !previousSelection.includes(v));
    let removed = previousSelection.filter((v) => !selectedValues.includes(v));

    if (added.length > 0) {
      added.forEach((value) => {
        this.trackAnalyticsEvent('selected', 'time-trend-filter', {
          filter_value: value,
        });
      });
    }

    if (removed.length > 0) {
      removed.forEach((value) => {
        this.trackAnalyticsEvent('deselected', 'time-trend-filter', {
          filter_value: value,
        });
      });
    }
  }

  @action
  onPrimaryMetricSelectionChange(selectedValues: string[]) {
    let previousSelection = this.selectedPrimaryMetrics;
    this.selectedPrimaryMetrics = selectedValues;

    let added = selectedValues.filter((v) => !previousSelection.includes(v));
    let removed = previousSelection.filter((v) => !selectedValues.includes(v));

    if (added.length > 0) {
      added.forEach((value) => {
        this.trackAnalyticsEvent('selected', 'primary-metric-filter', {
          filter_value: value,
        });
      });
    }

    if (removed.length > 0) {
      removed.forEach((value) => {
        this.trackAnalyticsEvent('deselected', 'primary-metric-filter', {
          filter_value: value,
        });
      });
    }
  }

  @action
  onWeekStartFilterClick() {
    this.trackAnalyticsEvent('clicked', 'week-start-filter');
  }

  @action
  onTimeTrendFilterClick() {
    this.trackAnalyticsEvent('clicked', 'time-trend-filter');
  }

  @action
  onPrimaryMetricFilterClick() {
    this.trackAnalyticsEvent('clicked', 'primary-metric-filter');
  }

  @action
  showHelpCenterArticle() {
    window.Intercom('showArticle', 11875255);
    this.trackAnalyticsEvent('clicked', 'help-center-link');
  }

  @action
  startFeedbackSurvey() {
    window.Intercom('startSurvey', 48184582);
    this.trackAnalyticsEvent('clicked', 'feedback-survey-link');
  }

  @action
  trackAnalyticsEvent(action: string, object: string, extraContext: Record<string, any> = {}) {
    this.intercomEventService.trackAnalyticsEvent({
      place: 'analyze/topic-trends',
      action,
      object,
      ...extraContext,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends': typeof TopicTrends;
  }
}
